var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useCreateAssistantStream } from 'app/domains/assistant/hooks/useCreateAssistantStream';
import { ChatMessageType } from 'shared/domains/apollo/generated/types';
import { isApolloError } from 'shared/domains/apollo/isApolloError';
import { RunWorkflowCheckDocument } from './RunWorkflowCheck.gql';
import { SetupWorkflowCheckDocument } from './SetupWorkflowCheck.gql';
import { SyncAssistantCreditsDocument } from './SyncAssistantCredits.gql';
import { WorkflowApprovalResultSchema, WorkflowSignatoryResultSchema } from './schemas';
import { useLatestWorkflowCheck } from './useLatestWorkflowCheck';
import { WorkflowCheckErrorSubcodes } from './types';
export const useStreamWorkflowCheck = (documentId, type) => {
    const { approvalResult, signatoryResult, addApprovalResultToCache, addSignatoryResultToCache, approvalWorkflows, signatoryWorkflows, loading, error: loadingError, } = useLatestWorkflowCheck(documentId);
    const createEventSource = useCreateAssistantStream();
    const [setupWorkflowMutation] = useMutation(SetupWorkflowCheckDocument);
    const [runWorkflowMutation] = useMutation(RunWorkflowCheckDocument);
    const [syncCreditsQuery] = useLazyQuery(SyncAssistantCreditsDocument);
    const [streaming, setStreaming] = useState(false);
    const [error, setError] = useState(null);
    const state = useMemo(() => {
        if (error) {
            return 'idle';
        }
        if (streaming) {
            return 'evaluating';
        }
        return loading ? 'loading' : 'idle';
    }, [loading, streaming, error]);
    const handleStreamResponse = (_a) => __awaiter(void 0, [_a], void 0, function* ({ chatId, streamToken }) {
        const evtSource = yield createEventSource(streamToken, chatId, 'document');
        const closeStream = () => {
            evtSource.close();
            setStreaming(false);
        };
        evtSource.onmessage = (event) => {
            const [, [kind, payload]] = JSON.parse(event.data);
            if (kind === 'END') {
                closeStream();
                return;
            }
            if (kind === 'ERROR') {
                setError('streaming');
                closeStream();
                return;
            }
            if (type === 'approval') {
                const { success, data } = WorkflowApprovalResultSchema.safeParse(JSON.parse(payload));
                if (success) {
                    addApprovalResultToCache(data);
                }
                else {
                    setError('streaming');
                    closeStream();
                }
            }
            else {
                const { success, data } = WorkflowSignatoryResultSchema.safeParse(JSON.parse(payload));
                if (success) {
                    addSignatoryResultToCache(data);
                }
                else {
                    setError('streaming');
                    closeStream();
                }
            }
        };
        // The server will close the connection after sending the end token, this is treated as
        // an error state by the EventSource, and we thus close the connection.
        evtSource.onerror = () => {
            closeStream();
        };
    });
    const setupWorkflow = (context) => __awaiter(void 0, void 0, void 0, function* () {
        const { data } = yield setupWorkflowMutation({ variables: { documentId } });
        const chatId = data === null || data === void 0 ? void 0 : data.createDocumentChat.id;
        if (!chatId) {
            throw new Error('Failed to create chat');
        }
        return { chatId, context };
    });
    const handleError = (err) => {
        var _a, _b;
        setStreaming(false);
        if (isApolloError(err)) {
            const subcode = (_b = (_a = err.graphQLErrors[0]) === null || _a === void 0 ? void 0 : _a.extensions) === null || _b === void 0 ? void 0 : _b.subcode;
            if (subcode === WorkflowCheckErrorSubcodes.InsufficientCredits) {
                syncCreditsQuery();
                setError('credits');
            }
        }
        else {
            setError('setup');
        }
    };
    const runWorkflow = (_b) => __awaiter(void 0, [_b], void 0, function* ({ chatId, workflowId, context, }) {
        var _c;
        const runWorkflowType = type === 'approval' ? ChatMessageType.WorkflowApprovalCheck : ChatMessageType.WorkflowSignatoriesSuggest;
        const { data } = yield runWorkflowMutation({ variables: { chatId, workflowId, context, type: runWorkflowType } });
        const streamToken = (_c = data === null || data === void 0 ? void 0 : data.lastChat.messages[0]) === null || _c === void 0 ? void 0 : _c.streamToken;
        if (!streamToken) {
            throw new Error('Failed to send workflow');
        }
        return { chatId, streamToken };
    });
    const startWorkflowCheck = (workflowId, context) => __awaiter(void 0, void 0, void 0, function* () {
        setStreaming(true);
        yield setupWorkflow(context)
            .then(({ chatId, context: ctx }) => runWorkflow({ chatId, workflowId, context: ctx }))
            .then(handleStreamResponse)
            .catch(handleError);
    });
    return {
        state,
        error: loadingError ? 'setup' : error,
        startWorkflowCheck,
        approvalResult,
        signatoryResult,
        approvalWorkflows,
        signatoryWorkflows,
    };
};
